import React from "react";
import ReactDOM from "react-dom/client";
import { HeadProvider } from "react-head";
import { ToastContainer } from "react-toastify";

import "react-toastify/dist/ReactToastify.min.css";

import "@fontsource/work-sans/latin.css";

import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  Routes,
  useLocation,
  useMatch,
  useSearchParams
} from "react-router-dom";

import "./index.css";

import { BlockedLayout } from "components/BlockedLayout";
import ChatbaseScript from "components/ChatbaseScript";
import { FeatureSwitchLayout, useFeatureSwitch } from "components/FeatureSwitch";
import * as Modal from "components/Modal";
import * as BillingModals from "v2/components/billing/modals";
import ProtectedBillingLayout from "components/ProtectedBillingLayout";
import ProtectedLayout from "components/ProtectedLayout";
import Sidebar from "components/Sidebar";
import { GlobalTooltip } from "components/Tooltip";
import TopBanners from "components/TopBanners";
import TrackRecentlyViewed from "components/TrackRecentlyViewed";

import BannedFromFreeLayout from "layouts/BannedFromFreeLayout";
import BeforeLoginLayout from "layouts/BeforeLoginLayout";
import BorderLayout from "layouts/BorderLayout";
import DynamicServicesProviderLayout from "layouts/DynamicServicesProviderLayout";
import OnboardingLayout from "layouts/OnboardingLayout";
import ThirdPartyServicesLayout from "layouts/ThirdPartyServicesLayout";

import ViewAllPlansModal from "v2/pages/billing/ViewAllPlansModal";
import ContactSalesModal from "v2/pages/billing/ContactSalesModal";
import PayAsYouGoModal from "v2/pages/billing/subscription/PayAsYouGoModal";
import BillingErrorModal from "v2/pages/billing/BillingErrorModal";

import { AnalyticsFiltersProvider, OverviewDataProvider, DomainDataProvider, ErrorLogsProvider } from "providers/AnalyticsProvider";
import ApiSettingsProvider from "providers/ApiSettingsProvider";
import UserProvider from "providers/UserProvider";
import OnboardTooltipProvider from "providers/OnboardingTooltipProvider";
import OfferSocialSignup from "routes/offer-social-signup";
import IssuesProvider from "providers/IssuesProvider";
import { HostedScrapingProjectsProvider } from "providers/HostedScrapingProvider";
import ApiCallsProvider from "providers/ApiCallsProvider";
import ProductFruitsProvider from "providers/ProductFruitsProvider";

import AuthError from "routes/auth-error";
import ForgotPassword from "routes/forgot-password";
import Login from "routes/login";
import MagicLink from "routes/magic-link";
import { SendActivationEmailNew } from "routes/send-activation-email";
import Signup from "routes/signup";
import ResetPassword from "routes/reset-password";

import BeautifulProjectsView from "routes/dashboard/BeautifulProjectsView";
import NewApiRequest from "routes/dashboard/NewApiRequest";
import NewProject from "routes/dashboard/NewProject";
import ProjectDetails from "routes/dashboard/ProjectDetails";
import ProjectEditPage from "routes/dashboard/ProjectEditPage";
import ProjectJobDetails from "routes/dashboard/ProjectJobDetails";

import * as ApiKeysDataRoute from "v2/api-key-management/routes/ApiKeysDataRoute";
import * as Authentication from "routes/dataroutes/Authentication";
import * as BillingData from "routes/dataroutes/BillingData";
import * as BillingDataV2 from "v2/dataroutes/BillingData";
import * as DashboardData from "routes/dataroutes/DashboardData";
import * as IssuesData from "routes/dataroutes/IssuesData";
import * as UserData from "routes/dataroutes/UserData";

import SidebarLayout from "routes/layout/SidebarLayout";
import TopBannersLayout from "routes/layout/TopBannersLayout";

import * as UserShortcuts from "routes/shortcuts/UserShortcuts";

import GlobalModals, { ApiKeyManagementModals } from "routes/GlobalModals";

import { getCookie, setCookie } from "utils/cookieUtils";

import * as HomeRoute from "v2/pages/home/HomeRoute";
import * as BillingRoute from "v2/pages/billing/BillingRoute";

import AnalyticsPage from "routes/dashboard/AnalyticsPage";
import DomainAnalyticsPage from "pages/analytics/DomainAnalyticsPage";
import DomainMultiplierModal from "components/common/DomainMultiplierModal";
import ErrorLogsPage from "pages/analytics/ErrorLogsPage";
import {
  ConfirmDisableAutoUpgradeModal, ConfirmChangePayAsYouGoModal,
  ConfirmEnableAutoUpgradeModal, ConfirmEnablePayAsYouGoModal, ConfirmResubscribeModal
} from "components/Modal/billing/PricingOptionsConfirmationModals";
import { PricingOptionsWarningPopupModal } from "components/pricingoptions/PricingOptionsWarningPopupModal";
import ChildrenOrOutlet from "components/ChildrenOrOutlet";
import { CrawlerProvider } from "providers/CrawlerProvider";
import ViewAllCrawlersPage from "pages/crawler/ViewAllCrawlersPage";
import CreateCrawlerPage from "pages/crawler/CreateCrawlerPage";
import EditCrawlerPage from "pages/crawler/EditCrawlerPage";
import ViewAllCrawlerJobsPage from "pages/crawler/ViewAllCrawlerJobsPage";
import ViewAllCrawlerJobScrapesPage from "pages/crawler/ViewAllCrawlerJobScrapesPage";
import { useNewBillingPage } from "v2/hooks/pilot/useNewBillingPage";
import PilotFeaturesProvider from "providers/PilotFeaturesProvider";
import { AutoOpenPaymentMethodDialog } from "pages/billing/AutoOpenPaymentMethodDialog";
import { AutoOpenBillingAddressDialog } from "pages/billing/AutoOpenBillingAddressDialog";
import MCPPage from "pages/mcp/MCPPage";
import MoreApisPage from "pages/more-apis/MoreApisPage";
import { AuthenticationProvider } from "providers/AuthenticationProvider";
import ChangeSubscriptionModal from "./v2/components/billing/modals/ChangeSubscriptionModal";
import FetcherDataCacheProvider from "./providers/FetcherDataCacheProvider";
import { ScrollToAnchorLayout } from "components/ScrollToAnchorLayout";
import { removeScheduledChangesAction } from "routes/data-routes/billing-data/active-subscription/actions/removeScheduledChangesAction";
import { DataRoutes } from "routes/data-routes";
import { BillingTabsRoutes } from "v2/pages/billing/tabs/BillingTabsRoutes";
import { ConfirmPaymentSourceSessionModal } from "v2/pages/billing/billing-details/ConfirmPaymentSourceSessionModal";


const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

declare global {
  interface Window {
    Chargebee: any;
    gtag: any;
    ZohoGCSettings: {
      isOpenDefault: boolean;
      needClose: boolean;
      removeMinimize: boolean;
    },
    chatbase: any;
    SupportWidget: {
      open: () => void;
      close: () => void;
    };
    SUPPORT_BOT_API: string;
    SUPPORT_BOT_TENANT: string;
  }

  namespace NodeJS {
    interface ProcessEnv {
      REACT_APP_GOOGLE_LOGIN_CLIENT_ID: string;
      REACT_APP_DOMAIN: string;
      REACT_APP_SUBDOMAIN: string;
      REACT_APP_PORT: string;
      REACT_APP_APP_ENV: "development" | "staging" | "production";
      REACT_APP_APP_CHARGEBEE_SITE_NAME: string;
      REACT_APP_APP_CHARGEBEE_PUBLISHABLE_KEY: string;
      REACT_APP_GITHUB_OAUTH_CLIENT_ID: string;
      REACT_APP_API_URL: string;
      REACT_APP_RECAPTCHA_V2_SITE_KEY: string;
      REACT_APP_NEW_SALES_BANNER_USERS: string;
      REACT_APP_PAYPAL_CLIENT_ID: string;
      REACT_APP_STRIPE_PAYPAL_CUSTOM_PAYMENT_METHOD_ID: string;
    }
  }
}

function NavigateToAccountActivationPage() {
  const [ searchParams ] = useSearchParams();

  return <Navigate to={ `/activate-account?${searchParams.toString()}` } replace />;
}

function NavigateToBillingSubscriptionTab() {
  const { isBillingV2Eligible } = useNewBillingPage();
  const isOnBillingPageRoot = useMatch({
    path: BillingRoute.path,
    end: true
  }) !== null;

  if (isBillingV2Eligible && isOnBillingPageRoot) {
    return <Navigate to="subscription" replace />;
  }

  return <></>;
}

const Root = () => {
  const location = useLocation();
  const state = location?.state as { backgroundLocation?: Location };
  const useNewApiKeyManagementDialogs = useFeatureSwitch("REACT_APP_USE_NEW_API_KEY_MANAGEMENT_DIALOGS");

  return (
    <>
      <Routes location={ state?.backgroundLocation || location }>
        <Route
          element={ <><ChatbaseScript /><ProtectedLayout/></> }
        >
          <Route
            element={
              <BorderLayout
                top={ <TopBanners/> }
                left={ <Sidebar/> }
                wrap="md"
                scrollMainWhenWrapped
              />
            }
          >

            {/* Analytics routes */}
            <Route path="/analytics" element={<AnalyticsFiltersProvider><Outlet /></AnalyticsFiltersProvider>}>
              <Route
                path=""
                element={<OverviewDataProvider><TrackRecentlyViewed location="/analytics" title="Analytics Overview" iconName="Status"><AnalyticsPage /></TrackRecentlyViewed></OverviewDataProvider>}
              >
                <Route path="filter" element={<Modal.FiltersModal />} />
                <Route path="customize-columns" element={<Modal.CustomizeColumnsModal />} />
              </Route>

              <Route
                path="domain-analytics"
                element={<DomainDataProvider><TrackRecentlyViewed location="/analytics/domain-analytics" title="Domain breakdown" iconName="Status"><DomainAnalyticsPage /></TrackRecentlyViewed></DomainDataProvider>}
              >
                <Route path="filter" element={<Modal.FiltersModal />} />
                <Route path="customize-columns" element={<Modal.CustomizeColumnsModal />} />
              </Route>

              <Route
                path="error-logs"
                element={<ErrorLogsProvider><TrackRecentlyViewed location="/analytics/error-logs" title="Error Logs" iconName="Status"><ErrorLogsPage /></TrackRecentlyViewed></ErrorLogsProvider>}
              >
                <Route path="filter" element={<Modal.ErrorFiltersModal />} />
                <Route path="customize-columns" element={<Modal.CustomizeColumnsModal />} />
              </Route>
            </Route>

            <Route path="/crawlers">
              <Route element={ <ProductFruitsProvider><CrawlerProvider><ChildrenOrOutlet /><PricingOptionsWarningPopupModal /></CrawlerProvider></ProductFruitsProvider> }>
                <Route index element={<TrackRecentlyViewed location="/crawlers" iconName="Status"><ViewAllCrawlersPage/></TrackRecentlyViewed>} />
                <Route path="new" element={<TrackRecentlyViewed location="/crawlers/new" iconName="Status"><CreateCrawlerPage/></TrackRecentlyViewed>} />
                <Route path=":id" element={<Navigate to="jobs" replace />} />
                <Route path=":id/jobs" element={<TrackRecentlyViewed location="/crawlers/:id/jobs" iconName="Status"><ViewAllCrawlerJobsPage/></TrackRecentlyViewed>} />
                <Route path=":id/jobs/:jobId" element={<TrackRecentlyViewed location="/crawlers/:id/jobs/:jobId" iconName="Status"><ViewAllCrawlerJobScrapesPage/></TrackRecentlyViewed>} />
                <Route path=":id/edit" element={<TrackRecentlyViewed location="/crawlers/:id/edit" iconName="Status"><EditCrawlerPage/></TrackRecentlyViewed>} />
              </Route>
            </Route>

            <Route
              element={ <BlockedLayout/> }
            >
              <Route
                path="/projects"
              >
                <Route index={ true } element={ <TrackRecentlyViewed iconName="DataPipeline"><BeautifulProjectsView/><PricingOptionsWarningPopupModal/></TrackRecentlyViewed> }/>
                <Route path="new" element={ <NewProject/> }/>
                <Route path=":projectId" element={ <TrackRecentlyViewed iconName="DataPipeline"><ProjectDetails/></TrackRecentlyViewed> }/>
                <Route path=":projectId/edit" element={ <ProjectEditPage/> }/>
                <Route path=":projectId/jobs/:jobId" element={ <TrackRecentlyViewed iconName="DataPipeline"><ProjectJobDetails/></TrackRecentlyViewed> }/>
              </Route>
            </Route>


            <Route
              path="/mcp"
              element={
                <TrackRecentlyViewed location="/mcp" title="MCP" iconName="MCP">
                  <MCPPage />
                </TrackRecentlyViewed>
              }
            >
              { GlobalModals() }
            </Route>

            <Route
              path="/more-apis"
              element={
                <TrackRecentlyViewed location="/more-apis" title="More APIs" iconName="MoreAPIs">
                  <MoreApisPage />
                </TrackRecentlyViewed>
              }
            >
              { GlobalModals() }
            </Route>

            <Route element={
              <BlockedLayout>
                <FeatureSwitchLayout featureSwitch="REACT_APP_API_PLAYGROUND_USERS"/>
              </BlockedLayout>
            }>
              <Route
                path="/apiplayground"
                element={ <TrackRecentlyViewed iconName="APIPlayground" /> }
              >
                <Route index={ true } element={ <><NewApiRequest/><PricingOptionsWarningPopupModal/></> }/>
              </Route>
            </Route>


            <Route path="/projectslist" element={ <Navigate to="/projects" replace/> }/>

            <Route
              path="/contact-support"
              element={ <Navigate to={ HomeRoute.path } state={ { openSupportWidget: true } }/> }
            />
            <Route
              path="/contact-sales"
              element={<Navigate to="/book-sales-call" state={ { backgroundLocation: HomeRoute.path } } />}
            />

            <Route element={ <BlockedLayout/> }>
              <Route
                path="/book-sales-call"
                element={ <Navigate to="/book-sales-call" state={ { backgroundLocation: HomeRoute.path } } /> }
              />
            </Route>

            <Route element={ <BannedFromFreeLayout/> }>
              <Route
                element={ <ProtectedBillingLayout/> }
              >
                <Route
                  path="/pay-now"
                  element={ <Modal.PayNowModal/> }
                />
              </Route>
            </Route>
          </Route>

          <Route path="/projectslist" element={ <Navigate to="/projects" replace/> }/>

          <Route
            path="/domain-multiplier"
            element={ <DomainMultiplierModal/> }
          />
          <Route
            path="/contact-support"
            element={ <Navigate to={ HomeRoute.path } state={ { openSupportWidget: true } }/> }
          />

          <Route element={ <BlockedLayout/> }>
            <Route
              path="/datapipeline/project/:projectId/error-report/:publicId"
              element={ <Modal.HostedScraperErrorReportModal/> }
            />
          </Route>
        </Route>

        {/*
          The API and potentially other links pointing to these urls.
          We need to redirect them to the appropriate route.
        */ }
        <Route
          path="/sendActivationEmail"
          element={ <Navigate to="/send-activation-email" replace/> }
        />
        <Route
          path="/activateAccount"
          element={ <NavigateToAccountActivationPage/> }
        />
        <Route path="/dashboard" element={ <Navigate to={ HomeRoute.path } replace/> }/>
        <Route path="/" element={ <Navigate to={ HomeRoute.path } replace/> }/>

      </Routes>

      { state?.backgroundLocation && (
        <Routes>
          <Route
            path="/datapipeline/project/:projectId/error-report/:publicId"
            element={ <Modal.HostedScraperErrorReportModal/> }
          />
          <Route
            path="/analytics/errored-request"
            element={<Modal.ErrorRequestModal />}
          />
          <Route
            element={
              <ProtectedLayout>
                <Outlet/>
              </ProtectedLayout>
            }
          >
            <Route
              path="/change-password"
              element={ <Modal.ChangePasswordModal/> }
            />
            <Route
              path="/domain-multiplier"
              element={ <DomainMultiplierModal/> }
            />
            <Route
              path="/contact-support"
              element={ <Navigate to={ HomeRoute.path } state={ { openSupportWidget: true } }/> }
            />

            <Route element={ <BlockedLayout/> }>
              { useNewApiKeyManagementDialogs ? (
                ApiKeyManagementModals()
              ) : (
                <>
                  <Route
                    path="/api-keys"
                    element={ <Modal.ApiKeyManagement /> }
                  />
                  <Route
                    path="/api-keys/confirm"
                    element={ <Modal.ConfirmApiKeyChangeModal /> }
                  />
                </>
              ) }
              <Route
                path="/book-sales-call"
                element={ <Modal.BookSalesCallModal/> }
              />

            </Route>

            <Route element={ <BannedFromFreeLayout/> }>
              <Route
                element={ <ProtectedBillingLayout/> }
              >
                <Route
                  path="/pay-now"
                  element={ <Modal.PayNowModal/> }
                />
              </Route>
            </Route>
          </Route>
        </Routes>
      ) }
    </>
  );
};

function ProvidersLayout() {
  return (
    <ApiCallsProvider>
      <ApiSettingsProvider>
        <UserProvider>
          <ProductFruitsProvider>
            <IssuesProvider>
              <OnboardTooltipProvider>
                <HostedScrapingProjectsProvider>
                  <Outlet />
                  <GlobalTooltip />
                </HostedScrapingProjectsProvider>
              </OnboardTooltipProvider>
            </IssuesProvider>
          </ProductFruitsProvider>
        </UserProvider>
      </ApiSettingsProvider>
    </ApiCallsProvider>
  );
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      id="auth"
      element={
        <ScrollToAnchorLayout>
          <AuthenticationProvider>
            <FetcherDataCacheProvider>
              <PilotFeaturesProvider>
                <Outlet/>
              </PilotFeaturesProvider>
            </FetcherDataCacheProvider>
          </AuthenticationProvider>
        </ScrollToAnchorLayout>
      }
      loader={ Authentication.checkSessionLoader }
    >
      <Route
        id={ UserData.ID }
        lazy={ () => import("routes/dataroutes/UserData") }
      >
        <Route
          element={ <>
            <ToastContainer
              position="bottom-right"
              closeButton={ false }
              closeOnClick={ false }
              draggable={ false }
              className="min-w-min" // without this the width of the toaster didn't match the width of its contents :/
            />
            <Outlet/>
          </> }
        >
          <Route
            element={ <DynamicServicesProviderLayout/> }
          >
            <Route
              element={ <ThirdPartyServicesLayout/> }
            >
              <Route
                element={ <BeforeLoginLayout/> }
              >
                <Route
                  path="/auth-error"
                  element={ <AuthError/> }
                />

                <Route
                  element={ <UserShortcuts.ToDashboardIfAlreadyAuthenticated/> }
                >
                  <Route
                    path="/login"
                    element={ <Login/> }
                    action={ Authentication.emailLoginAction }
                  />
                  <Route
                    path="/signup"
                    element={ <Signup/> }
                    action={ Authentication.emailSignupAction }
                  />
                  <Route
                    path="/offer-social-signup"
                    element={ <OfferSocialSignup/> }
                    action={ Authentication.socialSignupAction }
                  />
                  <Route
                    path="/forgot-password"
                    element={ <ForgotPassword/> }
                    action={ Authentication.forgotPasswordAction }
                  />
                  <Route
                    path="/reset"
                    element={ <ResetPassword/> }
                    action={ Authentication.resetPasswordAction }
                  />
                  <Route
                    path="/logout"
                    action={ Authentication.logoutAction }
                  />
                </Route> {/* UserShortcuts.ToDashboardIfAlreadyAuthenticated */ }

              </Route> {/* BeforeLoginLayout */ }

              <Route
                element={ <UserShortcuts.ToLoginIfNotAuthenticated/> /* part of old ProtectedLayout */ }
              >
                <Route
                  element={ <OnboardingLayout/> }
                >
                  <Route
                    path="/send-activation-email"
                    element={ <SendActivationEmailNew/> }
                  />
                </Route> {/* OnboardingLayout or AuthLayout */ }

                <Route
                  path="/magic-link"
                  element={ <MagicLink/> }
                />

              </Route> {/* UserShortcuts.ToLoginIfNotAuthenticated */ }

              <Route
                element={ <OnboardingLayout/> }
              >
                <Route
                  path="/activate-account"
                  lazy={ () => import("routes/activate-account") }
                />
              </Route>

              <Route
                element={ <UserShortcuts.ToLoginIfNotAuthenticated/> }
              >
                <Route
                  element={ <UserShortcuts.ToActivationPageIfDeactivated/> }
                >
                  <Route
                    element={ <>
                      <Outlet/>
                      <PricingOptionsWarningPopupModal/>
                      <ChatbaseScript />
                    </> }
                  >
                    <Route
                      Component={ TopBannersLayout }
                    >
                      <Route
                        Component={ SidebarLayout }
                      >
                        <Route
                          path={ HomeRoute.path }
                          Component={ HomeRoute.Component }
                        >
                          { GlobalModals() }
                        </Route>

                        <Route
                          path={ BillingRoute.path }
                          Component={ BillingRoute.Component }
                        >
                          <Route index element={ <NavigateToBillingSubscriptionTab /> } />

                          { Object.entries(BillingTabsRoutes).map(([ path, tabProps ]) =>
                            <Route
                              key={ path }
                              path={ path }
                              id={ tabProps.id }
                              element={ <><tabProps.Component /><Outlet /></> }
                              loader={ tabProps.loader }
                              action={ tabProps.action }
                            >
                              <Route path="plans">
                                {/* Routes accessible to banned_from_free users (upgrade path) */}
                                <Route element={ <BannedFromFreeLayout /> }>
                                  <Route index element={ <ViewAllPlansModal /> } />
                                  <Route path="upgrade/:planId" element={ <BillingModals.UpgradeModal isRedesign /> } />
                                  <Route path="contact-sales" element={ <ContactSalesModal /> } />
                                </Route>

                                {/* Routes blocked for ALL blocked users */}
                                <Route element={ <BlockedLayout /> }>
                                  <Route path="downgrade/:planId" element={ <ChangeSubscriptionModal /> } />
                                  <Route
                                    path="cancel"
                                    element={ <BillingModals.CancelSubscription.CancelModal isRedesign /> }
                                    loader={ BillingDataV2.notPaidInvoicesLoader }
                                  />
                                  <Route path="cancel-enterprise" element={ <BillingModals.CancelEnterpriseModal /> } />
                                  <Route
                                    path="cancellation-survey"
                                    element={ <BillingModals.CancelSubscription.CancellationSurveyModal /> }
                                    action={ BillingData.postCancellationSurveyAnswersAction }
                                  />
                                  <Route path="resubscribe/:planId" element={ <ConfirmResubscribeModal isRedesign /> } />
                                  <Route path="auto-upgrade/enable" element={ <ConfirmEnableAutoUpgradeModal isRedesign /> } />
                                  <Route path="auto-upgrade/disable" element={ <ConfirmDisableAutoUpgradeModal isRedesign /> } />
                                  <Route path="pay-as-you-go/change" element={ <ConfirmChangePayAsYouGoModal isRedesign /> } />
                                  <Route path="payg" element={ <PayAsYouGoModal /> } />
                                  <Route path="manage-plan/delete-account" element={ <BillingModals.V2DeleteAccountModal /> } />
                                </Route>
                              </Route>

                              <Route path="domain-multiplier" element={ <DomainMultiplierModal /> } />
                              { GlobalModals() }

                              { path === "details" && (
                                <Route
                                  path="payment-source/confirm-session/:checkoutId"
                                  element={ <ConfirmPaymentSourceSessionModal /> }
                                />
                              ) }
                            </Route>
                          ) }

                          <Route path="error" element={ <BillingErrorModal /> } />

                          {/* modals for the old design where there are no tabs on the page */}
                          <Route path="auto-upgrade/disable" element={ <ConfirmDisableAutoUpgradeModal /> } />
                          <Route path="auto-upgrade/enable" element={ <ConfirmEnableAutoUpgradeModal /> } />
                          <Route path="billing-address" element={ <AutoOpenBillingAddressDialog /> } />
                          <Route path="cancel-subscription" element={ <Modal.CancelSubscriptionModal/> } />
                          <Route path="cancel-enterprise" element={ <Modal.CancelEnterpriseModal/> } />
                          <Route path="coupons" element={ <Modal.CouponsModal /> } />
                          <Route path="delete-account" element={ <Modal.DeleteAccountModal/> } />
                          <Route path="keep-subscription" element={ <Modal.KeepSubscriptionModal /> } />
                          <Route path="pay-as-you-go/change" element={ <ConfirmChangePayAsYouGoModal /> } />
                          <Route path="pay-as-you-go/enable" element={ <ConfirmEnablePayAsYouGoModal /> } />
                          <Route path="payment-method" element={ <AutoOpenPaymentMethodDialog /> } />

                          <Route element={ <ProtectedBillingLayout /> } >
                            <Route path="cancel-subscription-change" element={ <Modal.CancelSubscriptionChangeModal /> } />
                            <Route path="downgrade-annual-subscription/:planId" element={ <Modal.DowngradeAnnualModal/> } />
                            <Route path="downgrade-subscription/:planId" element={ <Modal.DowngradeModal/> } />
                            <Route path="upgrade-annual-subscription/:planId" element={ <Modal.UpgradeAnnualModal/> } />
                            <Route path="upgrade-subscription/:planId" element={ <Modal.UpgradeModal/> } />
                            <Route path="confirm-renewal/:planId" element={ <Modal.ConfirmRenewalModal/> } />
                            <Route path="auto-renewal/clear" element={ <Modal.ClearAutoRenewalModal/> } />
                            <Route path="auto-renewal/:ceiling" element={ <Modal.AutoRenewalModal/> } />
                          </Route>

                          {/* modals for the new design where there are tabs on the page that have extra path segments */}
                          <Route path="*/coupons" element={ <Modal.CouponsModal /> } />
                          <Route path="*/auto-upgrade/enable" element={ <ConfirmEnableAutoUpgradeModal /> } />
                          <Route path="*/auto-upgrade/disable" element={ <ConfirmDisableAutoUpgradeModal /> } />
                          <Route path="*/pay-as-you-go/enable" element={ <ConfirmEnablePayAsYouGoModal /> } />
                          <Route path="*/pay-as-you-go/change" element={ <ConfirmChangePayAsYouGoModal /> } />


                          { GlobalModals() }
                        </Route>

                      </Route>
                    </Route>
                  </Route>

                </Route> {/* UserShortcuts.ToActivationPageIfDeactivated */ }
              </Route> {/* UserShortcuts.ToLoginIfNotAuthenticated */ }



              {/* Data Routes */}
              { DataRoutes.map(dataRoute => <Route key={ dataRoute.path } { ...dataRoute } />) }

              <Route
                path="/user-data"
              >
                <Route
                  path="delete-account"
                  action={ BillingDataV2.requestAccountDeletionAction }
                />
              </Route>

              <Route
                path="/billing-data"
              >
                <Route
                  path="invoices"
                  loader={ BillingDataV2.invoicesLoader }
                >
                  <Route
                    path="not-paid"
                    loader={ BillingData.notPaidInvoicesLoader }
                  />
                </Route>

                <Route
                  path="invoices-paginated"
                  loader={ BillingDataV2.invoicesPaginatedLoader }
                />

                <Route
                  path="billing-address"
                  loader={ BillingDataV2.billingAddressLoader }
                />

                <Route
                  path="payment-sources"
                  loader={ BillingDataV2.paymentSourcesLoader }
                  action={ BillingDataV2.paymentSourcesActions }
                  shouldRevalidate={ BillingDataV2.paymentSourcesShouldRevalidate }
                />

                <Route
                  path="billing-info"
                  loader={ BillingDataV2.billingInfoLoader }
                />

                <Route
                  path="subscription"
                >
                  <Route
                    path="cancel"
                    action={ BillingData.cancelSubscriptionAction }
                  />
                  <Route
                    path="cancellation-survey"
                    action={ BillingData.postCancellationSurveyAnswersAction }
                  />
                  {/* TODO this 'keep' path should not be supported anymore, as it's using a direct sub-action of the 'active-subscription' routes */}
                  <Route
                    path="keep"
                    action={ args => removeScheduledChangesAction(args) }
                  />
                  {/* TODO this is only available for legacy annual users. remove after all legacy annual plans have been archived */}
                  <Route
                    path="auto-renew-100"
                    action={ BillingDataV2.setAutoRenewalTo100Action }
                  />
                </Route>

                <Route
                  path="counters"
                  loader={ BillingDataV2.countersLoader }
                />
              </Route>

              <Route
                path="/dashboard-data"
              >
                <Route
                  path="changelog"
                  loader={ DashboardData.changelogLoader }
                />

                <Route
                  path="usage-history"
                  loader={ DashboardData.usageHistoryLoader }
                />
                <Route
                  path="per-domain-report"
                  loader={ DashboardData.downloadPerDomainReportLoader }
                />
              </Route>

              <Route
                path="/issues-data"
              >
                <Route
                  path="all"
                  loader={ IssuesData.allIssuesLoader }
                  shouldRevalidate={ () => false }
                />
                <Route
                  path="statuspage"
                  loader={ IssuesData.statuspageIssuesLoader }
                  shouldRevalidate={ () => false }
                />
              </Route>

              <Route
                path={ ApiKeysDataRoute.path }
                loader={ ApiKeysDataRoute.loader }
                action={ ApiKeysDataRoute.action }
              />

              <Route element={ <ProvidersLayout/> }>
                <Route path="*" Component={ Root }/>
              </Route>

            </Route> {/* ThirdPartyServicesLayout */ }
          </Route> {/* DynamicServicesProviderLayout */ }
        </Route> {/* ToastContainer */ }
      </Route> {/* UserData */ }
    </Route> // {/* GlobalNavigationProvider */ }
  ),
  {
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true
    }
  }
);

root.render(
  <React.StrictMode>
    <HeadProvider>
      <RouterProvider
        router={ router }
        future={ {
          // TODO still have to disable this future flag, possibly because of using <Suspense><Await> to resolve promises... should upgrade to react 19 and check React.use before enabling this flag
          // v7_startTransition: true
        } }
      />
    </HeadProvider>
  </React.StrictMode>
);


if (!getCookie("saFirstPage")) {
  setCookie(
    "saFirstPage",
    JSON.stringify({
      url: window.location.href,
      visited: new Date()
    })
  );

  setCookie(
    "saReferrer",
    document.referrer
  );
}
